import { CSSProperties, PropsWithChildren, forwardRef, ElementType } from 'react';
import { css, keyframes, Theme, useTheme } from '@emotion/react';
import { transparentize } from 'polished';
import { CheckCircle, Info, Warning, WarningCircle } from '@phosphor-icons/react';
import { Typography } from 'client/components/Typography';
import { borderRadius, mediaQuery } from 'client/Theme';

// TODO: This is most likely not good...
const errorPulseAnimation = (theme: Theme) => keyframes`
  0% { background-color: ${transparentize(0, theme.color.red[100])} }
  50% { background-color: ${transparentize(0.4, theme.color.red[100])} }
  100% { background-color: ${transparentize(0, theme.color.red[100])} }
`;

const bannerStyle = (theme: Theme) => css`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 16px;
  padding: 1rem;
  background-color: ${theme.color.grey[60]};
  border-radius: ${borderRadius}px;

  svg {
    flex-shrink: 0;
    color: inherit;
    opacity: 0.7;
  }

  a {
    text-decoration: underline;
    user-select: none;
    color: inherit;
    cursor: pointer;
  }

  ${mediaQuery.medium(css`
    flex-direction: row;
    align-items: center;
  `)}
`;

const contentStyle = css`
  display: flex;
  align-items: center;
  flex: 1;

  > .typography {
    flex: 1;
    margin-left: 0.9rem;
    color: inherit !important;
    opacity: 1;
  }

  > .MuiButtonBase-root {
    flex-shrink: 0;
    margin-top: 1rem;
    margin-bottom: -3px;

    ${mediaQuery.medium(css`
      margin-top: 0;
    `)}
  }
`;

const bannerFlashStyle = (theme: Theme) => css`
  animation-name: ${errorPulseAnimation(theme)};
  animation-duration: 500ms;
  animation-iteration-count: 2;
`;

export interface BannerProps {
  type?: 'info' | 'warning' | 'error' | 'success';
  icon?: JSX.Element | null;
  button?: JSX.Element;
  component?: ElementType<any>;
  style?: CSSProperties;
}

export const Banner = forwardRef<HTMLElement, PropsWithChildren<BannerProps>>(
  ({ type = 'info', icon, button = null, component = 'p', style = {}, children, ...other }, ref) => {
    const theme = useTheme();

    let visibleIcon: JSX.Element | null = null;
    if (type === 'info') visibleIcon = <Info size={16} weight="bold" />;
    if (type === 'warning') visibleIcon = <Warning size={16} weight="bold" />;
    if (type === 'error') visibleIcon = <WarningCircle size={16} weight="fill" />;
    if (type === 'success') visibleIcon = <CheckCircle size={16} weight="fill" />;
    if (icon || icon === null) visibleIcon = icon;

    let textColor = theme.color.foreground;
    if (type === 'warning') textColor = theme.color.yellow.text;
    if (type === 'error') textColor = theme.color.white;
    if (type === 'success') textColor = theme.color.primary.text;

    let backgroundColor = theme.color.grey[60];
    if (type === 'warning') backgroundColor = theme.color.yellow[60];
    if (type === 'error') backgroundColor = theme.color.red[100];
    if (type === 'success') backgroundColor = theme.color.primary[60];

    return (
      <section
        className={type}
        css={[bannerStyle, type === 'error' && bannerFlashStyle]}
        style={{ backgroundColor, color: textColor, ...style }}
        {...other}
        ref={ref}
      >
        <div css={contentStyle}>
          {visibleIcon}
          <Typography variant="body2" component={component}>
            {children}
          </Typography>
        </div>
        {button}
      </section>
    );
  }
);
